
import { 
  Sidebar, 
  SidebarProvider, 
  SidebarContent, 
  SidebarMenu, 
  SidebarMenuItem, 
  SidebarMenuButton,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarInset
} from "@/components/ui/sidebar";
import { Navbar } from "@/components/layout/Navbar";
import { 
  Newspaper, 
  FileSpreadsheet,
  Video, 
  ArrowRight,
  Users,
  HelpCircle,
  ExternalLink,
  Calendar
} from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";

interface MainLayoutProps {
  children: React.ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const { isAuthenticated } = useAuth();
  
  // External links data
  const externalLinks = [
    { name: "Web Star", url: "https://web-star.gr/" },
    { name: "TESAE", url: "https://www.tesae.gr/" },
    { name: "Epsilon Smart", url: "https://www.epsilonsmart.gr/" },
    { name: "Κράτηση Ραντεβού", url: "https://cal.com/web-star/15min", icon: Calendar }
  ];
  
  return (
    <TooltipProvider>
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <Sidebar>
            <SidebarContent>
              <SidebarGroup>
                <SidebarGroupLabel>Για όλους</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <RouterLink to="/">
                          <Newspaper />
                          <span>Νέα & Ενδιαφέροντα</span>
                        </RouterLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>

              <SidebarGroup>
                <SidebarGroupLabel>Εκπαιδευτικό Υλικό για Μέλη</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <RouterLink to="/resources/documents">
                          <FileSpreadsheet />
                          <span>Πρότυπες μελέτες</span>
                        </RouterLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <RouterLink to="/resources/videos">
                          <Video />
                          <span>Εκπαιδευτικά Video</span>
                        </RouterLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <RouterLink to="/resources/guides">
                          <ArrowRight />
                          <span>Goto Οδηγίες</span>
                        </RouterLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <RouterLink to="/courses/webinars">
                          <Users />
                          <span>Webinars</span>
                        </RouterLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild>
                        <RouterLink to="/qa">
                          <HelpCircle />
                          <span>Q&A</span>
                        </RouterLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>

              <SidebarGroup>
                <SidebarGroupLabel>Links</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {externalLinks.map((link, index) => (
                      <SidebarMenuItem key={index}>
                        <SidebarMenuButton asChild tooltip={link.url}>
                          <a href={link.url} target="_blank" rel="noopener noreferrer">
                            {link.icon ? <link.icon className="h-4 w-4" /> : <ExternalLink className="h-4 w-4" />}
                            <span>{link.name}</span>
                          </a>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
          </Sidebar>
          
          <SidebarInset>
            <Navbar />
            <div className="container mx-auto px-4 py-8">
              {children}
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </TooltipProvider>
  );
}
